import { useEffect, useState } from 'react';
import { pushNotificationsApi } from '../../api/notificationApis/push-notifications.api';
import { X, CheckCircle2 } from 'lucide-react';

// 🟢 Added onAllRead prop so we can tell the Navbar to clear the red dot
export const NotificationDropdown = ({ isOpen, onClose, onAllRead }: { isOpen: boolean, onClose: () => void, onAllRead: () => void }) => {
  const [notifications, setNotifications] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      pushNotificationsApi.getNotificationHistory()
        .then(setNotifications)
        .catch(console.error);
    }
  }, [isOpen]);

  // Handle single click
  const handleNotificationClick = async (id: string, isRead: boolean) => {
    if (!isRead) {
      try {
        await pushNotificationsApi.markAsRead(id);
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      } catch (error) {
        console.error("Failed to mark notification as read", error);
      }
    }
  };

  // 🟢 NEW: Handle Mark All As Read
  const handleMarkAllAsRead = async () => {
    try {
      await pushNotificationsApi.markAllAsRead();
      // Update UI instantly
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      // Tell Navbar to remove the red dot
      onAllRead();
    } catch (error) {
      console.error("Failed to mark all as read", error);
    }
  };

  // Check if there are any unread notifications left to determine if we show the button
  const hasUnread = notifications.some(n => !n.is_read);

  if (!isOpen) return null;

  return (
    <div className="absolute top-12 right-0 w-80 sm:w-96 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden animate-in fade-in zoom-in-95">
      
      <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
        <h4 className="font-bold text-sm text-gray-900">Notifications</h4>
        
        <div className="flex items-center gap-3">
          {/* 🟢 NEW: Mark all as read button */}
          {hasUnread && (
            <button 
              onClick={handleMarkAllAsRead}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Mark all as read
            </button>
          )}
          <button 
            onClick={onClose}
            className="hover:bg-gray-200 rounded-full p-1 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>
      
      <div className="max-h-[350px] overflow-y-auto overscroll-contain">
        {notifications.length === 0 ? (
          <p className="p-8 text-center text-xs text-gray-400">
            No new notifications
          </p>
        ) : (
          notifications.map((n) => (
            <div 
              key={n.id} 
              onClick={() => handleNotificationClick(n.id, n.is_read)}
              className={`p-4 border-b border-gray-50 transition-colors cursor-pointer 
                ${!n.is_read ? 'bg-blue-50/40 hover:bg-blue-50' : 'hover:bg-gray-50'}`
              }
            >
              <div className="flex justify-between items-start">
                <p className={`text-sm ${!n.is_read ? 'font-bold text-blue-900' : 'font-semibold text-gray-900'}`}>
                  {n.title}
                </p>
                {!n.is_read && <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>}
              </div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{n.message}</p>
              <span className="text-[10px] font-medium text-gray-400 mt-2 block">
                {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))
        )}
      </div>

    </div>
  );
};